// Components Library
import { useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { UserContext } from "../../context/User";
import { Toaster,toast } from "react-hot-toast";
// Function Imports
import {
  auth,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  googleProvider,
} from "../../firebase";

import Background from "./background.jpg";
import {
  getStripeSubscribePaymentLinkUrl,
  hasMongoStripeSubscriptionId,
} from "../../utils/stripeSubscribePaymentLink";
import { isSubscriptionAccessBlocked } from "../../utils/subscriptionStatus";

const INACTIVE_ACCOUNT_MESSAGE =
  "Your account is not active. Please check the received verification email for the registered email address.";

/** Mongo `customerconfirmation` sets 1 or "1" when the user has activated their email link. */
const isMongoEmailVerified = (status) =>
  status === 1 || status === "1" || String(status) === "1";

const Login = () => {
  // Variables
  const navigate = useNavigate();

  // States
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errorMessage, setErrorMessage] = useState();
  const [loading, setLoading] = useState(false);
  const [pendingVerification, setPendingVerification] = useState(false);
  const [resendVerificationLoading, setResendVerificationLoading] =
    useState(false);
  const [resendVerificationError, setResendVerificationError] = useState("");
  /** How the user signed in before we blocked them (for resend re-auth). */
  const [verificationResendMethod, setVerificationResendMethod] = useState(
    null
  );

  // context
  const { changeUserType } = useContext(UserContext);

  // Function 1 - Handle email input change
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
    setPendingVerification(false);
    setVerificationResendMethod(null);
    setResendVerificationError("");
    setErrorMessage("");
  };

  // Function 2 - Handle password input change
  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
    setPendingVerification(false);
    setVerificationResendMethod(null);
    setResendVerificationError("");
    setErrorMessage("");
  };

  const getUserRedirectPath = (user) =>
    isSubscriptionAccessBlocked(user.subscriptionStatus) ? "/settings" : "/";

  const handleSuccessfulLogin = async (user, signInMethod = "email") => {
    if ("userType" in user && user.userType === "Admin") {
      changeUserType("admin");
      localStorage.setItem("userType", "admin");
      toast.dismiss();
      setPendingVerification(false);
      setVerificationResendMethod(null);
      navigate("/admin");
      return;
    }

    if (!isMongoEmailVerified(user.confirmation_status)) {
      setVerificationResendMethod(signInMethod);
      setErrorMessage(INACTIVE_ACCOUNT_MESSAGE);
      setPendingVerification(true);
      try {
        await signOut(auth);
      } catch (e) {
        console.error(e);
      }
      localStorage.removeItem("userType");
      changeUserType("");
      return;
    }

    changeUserType("user");
    localStorage.setItem("userType", "user");
    toast.dismiss();
    setPendingVerification(false);
    setVerificationResendMethod(null);

    if (hasMongoStripeSubscriptionId(user)) {
      navigate(getUserRedirectPath(user));
      return;
    }

    const subscribeUrl = getStripeSubscribePaymentLinkUrl(
      user.userId,
      user.email
    );
    if (subscribeUrl) {
      window.location.href = subscribeUrl;
      return;
    }

    navigate(getUserRedirectPath(user));
  };

  const resendVerificationEmail = async () => {
    setResendVerificationError("");
    setResendVerificationLoading(true);
    let reauthed = false;
    try {
      let idToken;
      if (verificationResendMethod === "google") {
        const cred = await signInWithPopup(auth, googleProvider);
        reauthed = true;
        const fetchRes = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/users/${cred.user.uid}`
        );
        const mongoUser = await fetchRes.json();
        if (
          !fetchRes.ok ||
          mongoUser.error ||
          isMongoEmailVerified(mongoUser.confirmation_status)
        ) {
          setResendVerificationError(
            "This Google account is not waiting for email verification."
          );
          return;
        }
        idToken = await cred.user.getIdToken();
      } else {
        if (!password.trim()) {
          setResendVerificationError(
            "Enter your password above, then try resending again."
          );
          return;
        }
        const cred = await signInWithEmailAndPassword(
          auth,
          email,
          password
        );
        reauthed = true;
        idToken = await cred.user.getIdToken();
      }

      const response = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/users/resend-verification-email`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ idToken }),
        }
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setResendVerificationError(
          data.error || "Could not resend the verification email."
        );
        return;
      }
      toast.success(
        "Verification email sent. Check your inbox (and spam folder)."
      );
    } catch (err) {
      if (err?.code === "auth/popup-closed-by-user") {
        setResendVerificationError("Sign-in was cancelled.");
      } else {
        setResendVerificationError(
          err?.message || "Could not resend the verification email."
        );
      }
    } finally {
      if (reauthed) {
        try {
          await signOut(auth);
        } catch (e) {
          /* ignore */
        }
      }
      setResendVerificationLoading(false);
    }
  };

  // Function 3 - Email log-in
  const emailLogin = async () => {
    setPendingVerification(false);
    setVerificationResendMethod(null);
    setResendVerificationError("");

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (email === "") {
      setErrorMessage("Please Enter Email Address.");
      setLoading(false);
      return;
    } else if (!emailRegex.test(email)) {
      setErrorMessage("Please Enter a Valid Email Address.");
      setLoading(false);
      return;
    } else if (password === "") {
      setErrorMessage("Please Enter Password.");
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password);
      const fetchRes = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/users/${cred.user.uid}`
      );
      const user = await fetchRes.json();
      if (!fetchRes.ok || user.error) {
        await signOut(auth);
        setErrorMessage("Sign-in failed. Please try again.");
        return;
      }
      await handleSuccessfulLogin(user, "email");
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Function 4 - Google sign-in (login only — no registration; existing app users only)
  const googleLogin = () => {
    setErrorMessage();
    setPendingVerification(false);
    setVerificationResendMethod(null);
    setResendVerificationError("");
    setLoading(true);
    signInWithPopup(auth, googleProvider)
      .then(async (userCredentials) => {
        const fetchRes = await fetch(
          `${process.env.REACT_APP_SERVER_URL}/users/${userCredentials.user.uid}`
        );
        const user = await fetchRes.json();
        if (!fetchRes.ok || user.error) {
          await signOut(auth);
          setErrorMessage("Sign-in failed. Please try again.");
          return;
        }
        await handleSuccessfulLogin(user, "google");
      })
      .catch((error) => {
        setErrorMessage(error.message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <div className="w-screen h-screen flex justify-center items-center bg-gray-900 relative px-8 lg:px-0">
      <div className="absolute bottom-16 md:bottom-8 z-50 flex justify-center space-x-10 font-sans font-medium text-xs text-gray-300">
        <a
          href="https://www.iubenda.com/privacy-policy/45330845"
          target={"_blank"}
          rel="noreferrer"
          className="cursor-pointer hover:underline hover:text-white"
        >
          Privacy Policy
        </a>
        <a
          href="https://www.iubenda.com/privacy-policy/45330845/cookie-policy"
          target={"_blank"}
          rel="noreferrer"
          className="cursor-pointer hover:underline hover:text-white"
        >
          Cookie Policy
        </a>
        <a
          href="https://www.iubenda.com/terms-and-conditions/45330845"
          target={"_blank"}
          rel="noreferrer"
          className="cursor-pointer hover:underline hover:text-white"
        >
          Terms & Conditions
        </a>
      </div>
      <img
        className="object-cover lg:object-stretch h-full w-full absolute inset-0 pointer-events-none"
        src={Background}
        alt=""
      />

      <div className="w-full flex flex-col items-center z-50">
        {/* Container */}
        <div className="w-full max-w-lg h-full bg-white rounded-3xl shadow-sm p-8 sm:max-w-sm">
          <div
            onClick={() => googleLogin()}
            className="w-full bg-white border border-gray-200 h-10 rounded-full flex space-x-2 justify-center items-center mb-8 hover:border-gray-300 cursor-pointer group"
          >
            <svg
              className="w-4 h-4"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18.1712 8.36824H17.5V8.33366H9.99996V11.667H14.7095C14.0225 13.6074 12.1762 15.0003 9.99996 15.0003C7.23871 15.0003 4.99996 12.7616 4.99996 10.0003C4.99996 7.23908 7.23871 5.00033 9.99996 5.00033C11.2745 5.00033 12.4341 5.48116 13.317 6.26658L15.6741 3.90949C14.1858 2.52241 12.195 1.66699 9.99996 1.66699C5.39788 1.66699 1.66663 5.39824 1.66663 10.0003C1.66663 14.6024 5.39788 18.3337 9.99996 18.3337C14.602 18.3337 18.3333 14.6024 18.3333 10.0003C18.3333 9.44158 18.2758 8.89616 18.1712 8.36824Z"
                fill="#FBC02D"
              />
              <path
                d="M2.62756 6.12157L5.36548 8.12949C6.10631 6.29533 7.90048 5.00033 10.0001 5.00033C11.2746 5.00033 12.4342 5.48116 13.3171 6.26657L15.6742 3.90949C14.1859 2.52241 12.1951 1.66699 10.0001 1.66699C6.79923 1.66699 4.0234 3.47408 2.62756 6.12157Z"
                fill="#E53935"
              />
              <path
                d="M9.99993 18.3336C12.1524 18.3336 14.1083 17.5099 15.587 16.1703L13.0078 13.9878C12.1712 14.6215 11.1312 15.0003 9.99993 15.0003C7.83243 15.0003 5.99201 13.6182 5.29868 11.6895L2.58118 13.7832C3.96034 16.482 6.76118 18.3336 9.99993 18.3336Z"
                fill="#4CAF50"
              />
              <path
                d="M18.1713 8.36759L18.1646 8.33301H17.5H10V11.6663H14.7096C14.3796 12.5984 13.78 13.4022 13.0067 13.9876C13.0071 13.9872 13.0075 13.9872 13.0079 13.9868L15.5871 16.1693C15.4046 16.3351 18.3333 14.1663 18.3333 9.99967C18.3333 9.44092 18.2758 8.89551 18.1713 8.36759Z"
                fill="#1565C0"
              />
            </svg>

            <span className="font-sans font-medium text-gray-900 text-sm ">
              Continue with Google
            </span>
          </div>
          <div className="w-full h-px bg-gray-200 flex justify-center items-center mb-8">
            <span className="px-4 bg-white font-sans font-normal text-sm text-gray-400">
              or
            </span>
          </div>

          <span className="font-sans text-gray-900 text-xs font-medium">
            Email Address <span style={{ color: "red" }}>*</span>
          </span>
          <input
            onChange={(event) => handleEmailChange(event)}
            placeholder="johndoe@gmail.com"
            className="h-12 w-full mt-2 mb-6 border border-gray-200 rounded-xl font-sans px-4 font-medium text-gray-900 text-sm hover:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-600"
          />
          <span className="font-sans text-gray-700 text-xs font-medium">
            Password <span style={{ color: "red" }}>*</span>
          </span>
          <input
            type="password"
            onChange={(event) => handlePasswordChange(event)}
            placeholder="·························"
            className="h-12 w-full mt-2 border border-gray-200 rounded-xl font-sans px-4 font-medium text-gray-900 text-sm hover:border-blue-600 focus:outline-none focus:ring-4 focus:ring-blue-200 focus:border-blue-600"
          />
          {errorMessage && (
            <div className="mt-6 w-full px-2 py-1 bg-red-50 border border-red-100 rounded-md font-sans font-medium text-red-500 text-xs">
              {pendingVerification ? (
                <p className="m-0 leading-relaxed">
                  {INACTIVE_ACCOUNT_MESSAGE}{" "}
                  <a
                    href="#resend-verification"
                    className={`font-semibold text-blue-600 hover:text-blue-800 hover:underline ${
                      resendVerificationLoading
                        ? "pointer-events-none opacity-60"
                        : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      if (!resendVerificationLoading) {
                        resendVerificationEmail();
                      }
                    }}
                    aria-busy={resendVerificationLoading}
                  >
                    {resendVerificationLoading
                      ? "Sending…"
                      : "Click here to resend the verification email."}
                  </a>
                </p>
              ) : (
                errorMessage
              )}
              {pendingVerification && resendVerificationError && (
                <p className="mt-2 mb-0 text-red-600 font-medium">
                  {resendVerificationError}
                </p>
              )}
            </div>
          )}
          <button
            onClick={() => emailLogin()}
            className={`${
              loading
                ? "bg-blue-300 pointer-events-none"
                : "bg-blue-600 hover:bg-blue-700"
            } mt-8 w-full h-10  text-white font-sans font-semibold text-sm rounded-full`}
          >
            Log in
          </button>

          <div className="flex mt-4 w-full justify-center">
            <span className="font-sans text-gray-700 text-xs font-medium">

              <span
                onClick={() => navigate("/forget_password")}
                className="text-blue-500 hover:underline cursor-pointer"
              >
               Forgot Password
              </span>

            </span>
          </div>

          <div className="flex mt-4 w-full justify-center">
            <span className="font-sans text-gray-700 text-xs font-medium">
              New to Haste?{" "}
              <span
                onClick={() => navigate("/register")}
                className="text-blue-500 hover:underline cursor-pointer"
              >
                Get started
              </span>
              .
          </span>
          </div>
        </div>
      </div>
      <Toaster position="top-center" reverseOrder={false} />
    </div>
  );
};

export default Login;
